import { useEffect, useState } from "react";
import { Archive, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { usePlatformCan } from "../../lib/platform";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";

const FIELDS = [
  ["technical_logs_months", "Journaux techniques (mois)", "Tentatives de connexion, sessions expirées, codes, scans QR, journal d'emails, cache d'adresses."],
  ["audit_logs_months", "Journal d'audit (mois)", "Actions administratives et modifications sensibles."],
  ["closed_feedback_anonymize_months", "Anonymisation des feedbacks clôturés (mois)", "Coordonnées, compte lié, pièces jointes et codes d'accès retirés ; le contenu reste dans les statistiques."],
  ["inactive_account_flag_months", "Signalement des comptes inactifs (mois)", "Les comptes usagers sans connexion sont signalés, jamais supprimés automatiquement."],
];

const SettingsForm = ({ initial, canManage, onSaved }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(initial); }, [initial]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/retention/settings", Object.fromEntries(Object.entries(f).filter(([k]) => k in initial).map(([k, v]) => [k, typeof v === "boolean" ? v : Number(v)]))); toast.success("Règles enregistrées"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface space-y-4 p-6" data-testid="retention-settings">
      {FIELDS.map(([k, label, help]) => (
        <div key={k} className="grid gap-2 sm:grid-cols-3 sm:items-center">
          <div className="sm:col-span-2"><p className="text-sm font-medium">{label}</p><p className="text-xs text-slate-500">{help}</p></div>
          <Input type="number" min={1} max={120} value={f[k]} disabled={!canManage} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`retention-${k}`} />
        </div>
      ))}
      <label className="flex items-center justify-between gap-4 border-t border-slate-100 pt-4 text-sm"><span><b>Purge automatique quotidienne</b><span className="block text-xs text-slate-500">Exécutée chaque jour à 3 h UTC, chaque exécution est journalisée.</span></span>
        <Switch checked={!!f.auto_purge} disabled={!canManage} onCheckedChange={(v) => setF({ ...f, auto_purge: v })} data-testid="retention-auto-purge" /></label>
      {canManage && <Button onClick={save} disabled={busy} className="rounded-full" data-testid="retention-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Enregistrer</Button>}
    </div>
  );
};

const Runs = ({ runs }) => (
  <div className="card-surface p-6" data-testid="retention-runs">
    <p className="mb-3 font-display font-semibold">Journal des purges</p>
    {!runs.length && <p className="text-sm text-slate-500">Aucune exécution pour l'instant.</p>}
    <ul className="divide-y divide-slate-100 text-sm">
      {runs.map((r) => (
        <li key={r.id} className="py-2.5" data-testid={`retention-run-${r.id}`}>
          <p><b>{new Date(r.started_at).toLocaleString("fr-FR")}</b> · {r.trigger === "cron" ? "automatique" : "manuelle"} · <span className={r.status === "ok" ? "text-emerald-700" : "text-rose-700"}>{r.status === "ok" ? "réussie" : "erreurs"}</span></p>
          <p className="text-xs text-slate-500">{Object.entries(r.counts).filter(([, n]) => n).map(([k, n]) => `${k} : ${n}`).join(" · ") || "Rien à purger"}</p>
          {r.errors?.length > 0 && <p className="text-xs text-rose-600">{r.errors.join(" · ")}</p>}
        </li>
      ))}
    </ul>
  </div>
);

export default function AdminRetention() {
  const can = usePlatformCan();
  const settings = useApi("/admin/retention/settings");
  const runs = useApi("/admin/retention/runs");
  const inactive = useApi(can("users:read") ? "/admin/retention/inactive-users" : null);
  const [busy, setBusy] = useState(false);
  const runNow = async () => {
    setBusy(true);
    try { await api.post("/admin/retention/run"); toast.success("Purge exécutée"); runs.reload(); inactive.reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (settings.loading) return <Loader />;
  if (settings.error) return <ErrorState message={settings.error} onRetry={settings.reload} />;
  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title="Données & conservation" description="Durées de conservation, purge et anonymisation des données personnelles."
        actions={can("settings:manage") && <Button onClick={runNow} disabled={busy} variant="outline" className="rounded-full" data-testid="retention-run-now">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}Exécuter maintenant</Button>} />
      <SettingsForm initial={settings.data.settings} canManage={can("settings:manage")} onSaved={settings.reload} />
      {runs.data && <Runs runs={runs.data} />}
      {inactive.data && (
        <div className="card-surface p-6" data-testid="retention-inactive">
          <p className="mb-3 flex items-center gap-2 font-display font-semibold"><Archive className="h-4 w-4" />Comptes inactifs signalés ({inactive.data.length})</p>
          {!inactive.data.length && <p className="text-sm text-slate-500">Aucun compte signalé.</p>}
          <ul className="divide-y divide-slate-100 text-sm">{inactive.data.map((u) => <li key={u.id} className="py-2">{u.name} · <span className="text-slate-500">{u.email} · dernière connexion : {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("fr-FR") : "inconnue"}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}
